import { useRef, useState } from 'react';
import { MONTH_NAMES, DAY_LABELS, formatDate, groupScheduleByMonth, toDateKey, getSessionLabels } from '../utils/scheduler';

const NONE = '__none__';

function PersonSelect({ label, value, options, onChange }) {
  return (
    <div className="form-group">
      <label>{label}</label>
      <select className="form-control" value={value ?? NONE} onChange={e => onChange(e.target.value === NONE ? null : e.target.value)}>
        <option value={NONE}>— 없음 —</option>
        {options.map(m => (
          <option key={m.id} value={m.id}>
            {m.name}{m.active === false ? ' (휴식)' : ''}
          </option>
        ))}
      </select>
    </div>
  );
}

function EditPanel({ session, persons, ushers, overrides, onSave, onCancel }) {
  const dateKey = toDateKey(session.date);
  const ov = overrides[dateKey] ?? {};

  const isWed = session.dayType === 'wednesday';

  const [noonFacId,    setNoonFacId]    = useState(ov.noonFacilitatorId    ?? session.noonFacilitator?.id    ?? null);
  const [eveningFacId, setEveningFacId] = useState(ov.eveningFacilitatorId ?? session.eveningFacilitator?.id ?? null);
  const [noonPrayId,   setNoonPrayId]   = useState(ov.noonPrayerId         ?? session.noonPrayer?.id         ?? null);
  const [eveningPrayId,setEveningPrayId]= useState(ov.eveningPrayerId      ?? session.eveningPrayer?.id      ?? null);
  const [noonUsherIds, setNoonUsherIds] = useState(ov.noonUsherIds ?? (session.noonUshers ?? []).map(u => u.id));
  const [eveUsherIds,  setEveUsherIds]  = useState(ov.eveUsherIds  ?? (session.eveUshers  ?? []).map(u => u.id));
  const [usherIds,     setUsherIds]     = useState(ov.usherIds ?? session.ushers.map(u => u.id));

  function toggleNoonUsher(id) { setNoonUsherIds(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]); }
  function toggleEveUsher(id)  { setEveUsherIds (prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]); }
  function toggleUsher(id)     { setUsherIds    (prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]); }

  function handleSave() {
    if (isWed) {
      onSave(dateKey, { noonFacilitatorId: noonFacId, eveningFacilitatorId: eveningFacId,
        noonPrayerId: noonPrayId, eveningPrayerId: eveningPrayId,
        noonUsherIds, eveUsherIds });
    } else {
      onSave(dateKey, { noonFacilitatorId: noonFacId, eveningFacilitatorId: eveningFacId,
        noonPrayerId: noonPrayId, eveningPrayerId: eveningPrayId, usherIds });
    }
  }

  // 수동 수정이므로 전체 persons를 선택지로 제공
  const facOptions  = persons;
  const prayOptions = persons;

  return (
    <tr>
      <td colSpan={5} style={{ padding: 0 }}>
        <div style={{ background: '#fffbeb', border: '1.5px solid #fbbf24', borderRadius: 8, margin: '4px 8px 8px', padding: 16 }}>
          <div style={{ fontWeight: 700, fontSize: '0.85rem', marginBottom: 14, color: '#92400e' }}>
            ✏️ {formatDate(session.date)} 실제 배정 수정
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 1fr))', gap: 10, marginBottom: 14 }}>
            <PersonSelect label={`🎤 ${getSessionLabels(session.dayType).first} 사회`}  value={noonFacId}     options={facOptions}  onChange={setNoonFacId} />
            <PersonSelect label={`🎤 ${getSessionLabels(session.dayType).second} 사회`} value={eveningFacId}  options={facOptions}  onChange={setEveningFacId} />
            <PersonSelect label={`🙏 ${getSessionLabels(session.dayType).first} 기도`}  value={noonPrayId}    options={prayOptions} onChange={setNoonPrayId} />
            <PersonSelect label={`🙏 ${getSessionLabels(session.dayType).second} 기도`} value={eveningPrayId} options={prayOptions} onChange={setEveningPrayId} />
          </div>

          {isWed ? (
            <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', marginBottom: 14 }}>
              {[
                { label: '🚪 정오 안내위원', ids: noonUsherIds, toggle: toggleNoonUsher },
                { label: '🚪 저녁 안내위원', ids: eveUsherIds,  toggle: toggleEveUsher  },
              ].map(({ label, ids, toggle }) => (
                <div key={label} className="form-group" style={{ flex: 1, minWidth: 200, marginBottom: 0 }}>
                  <label>{label}</label>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 4 }}>
                    {persons.map(p => (
                      <label key={p.id} style={{ display: 'flex', alignItems: 'center', gap: 5, fontSize: '0.85rem', cursor: 'pointer',
                        padding: '4px 10px', borderRadius: 6, border: '1.5px solid',
                        borderColor: ids.includes(p.id) ? 'var(--success)' : 'var(--border)',
                        background: ids.includes(p.id) ? '#f0fdf4' : 'var(--bg)', color: 'var(--text)',
                      }}>
                        <input type="checkbox" checked={ids.includes(p.id)} onChange={() => toggle(p.id)} />
                        {p.name}
                      </label>
                    ))}
                    {persons.length === 0 && <span style={{ color: 'var(--text-muted)', fontSize: '0.82rem' }}>등록된 인원이 없습니다</span>}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="form-group" style={{ marginBottom: 14 }}>
              <label>🚪 로비 안내위원</label>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginTop: 4 }}>
                {persons.map(p => (
                  <label key={p.id} style={{ display: 'flex', alignItems: 'center', gap: 5, fontSize: '0.85rem', cursor: 'pointer',
                    padding: '4px 10px', borderRadius: 6, border: '1.5px solid',
                    borderColor: usherIds.includes(p.id) ? 'var(--success)' : 'var(--border)',
                    background: usherIds.includes(p.id) ? '#f0fdf4' : 'var(--bg)', color: 'var(--text)',
                  }}>
                    <input type="checkbox" checked={usherIds.includes(p.id)} onChange={() => toggleUsher(p.id)} />
                    {p.name}
                  </label>
                ))}
                {persons.length === 0 && <span style={{ color: 'var(--text-muted)', fontSize: '0.82rem' }}>등록된 인원이 없습니다</span>}
              </div>
            </div>
          )}

          <div style={{ display: 'flex', gap: 8 }}>
            <button className="btn btn-primary btn-sm" onClick={handleSave}>💾 저장</button>
            <button className="btn btn-outline btn-sm" onClick={onCancel}>취소</button>
            {overrides[dateKey] && (
              <button className="btn btn-sm" style={{ background: '#fee2e2', color: '#dc2626', border: 'none' }}
                onClick={() => onSave(dateKey, null)}>
                ↩ 자동 배정으로 초기화
              </button>
            )}
          </div>
        </div>
      </td>
    </tr>
  );
}

function SessionRow({ s, overrides, persons, ushers, onOverride }) {
  const [editing, setEditing] = useState(false);
  const isWed = s.dayType === 'wednesday';
  const isOverridden = !!overrides[toDateKey(s.date)];

  function handleSave(dateKey, data) {
    onOverride(dateKey, data);
    setEditing(false);
  }

  return (
    <>
      <tr className={isWed ? 'row-wed' : 'row-sun'}>
        <td className="date-cell">
          <strong>{formatDate(s.date)}</strong>
          {isOverridden && (
            <span title="수동 수정됨" style={{ marginLeft: 5, fontSize: '0.65rem', background: '#fef3c7',
              color: '#92400e', padding: '1px 5px', borderRadius: 4, fontWeight: 700 }}>수정</span>
          )}
        </td>
        <td>
          <span className={isWed ? 'day-label-wed' : 'day-label-sun'}>{DAY_LABELS[s.dayType]}</span>
        </td>
        <td>
          <div className="session-row"><span className="session-label label-noon">{getSessionLabels(s.dayType).first}</span><span className="fac-name">{s.noonFacilitator?.name ?? '—'}</span></div>
          <div className="session-row"><span className="session-label label-evening">{getSessionLabels(s.dayType).second}</span><span className="fac-name">{s.eveningFacilitator?.name ?? '—'}</span></div>
        </td>
        <td>
          <div className="session-row"><span className="session-label label-noon">{getSessionLabels(s.dayType).first}</span><span className="fac-name">{s.noonPrayer?.name ?? '—'}</span></div>
          <div className="session-row"><span className="session-label label-evening">{getSessionLabels(s.dayType).second}</span><span className="fac-name">{s.eveningPrayer?.name ?? '—'}</span></div>
        </td>
        <td>
          <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 8 }}>
            <div>
              {isWed ? (
                <>
                  <div className="session-row">
                    <span className="session-label label-noon">정오</span>
                    <span className="usher-names" style={{ display: 'inline' }}>
                      {s.noonUshers?.length > 0 ? s.noonUshers.map(u => u.name).join(', ') : '—'}
                    </span>
                  </div>
                  <div className="session-row">
                    <span className="session-label label-evening">저녁</span>
                    <span className="usher-names" style={{ display: 'inline' }}>
                      {s.eveUshers?.length > 0 ? s.eveUshers.map(u => u.name).join(', ') : '—'}
                    </span>
                  </div>
                </>
              ) : (
                <div className="usher-names">{s.ushers.length > 0 ? s.ushers.map(u => u.name).join(', ') : '—'}</div>
              )}
            </div>
            <button
              className="btn btn-sm"
              style={{ flexShrink: 0, background: editing ? '#fef3c7' : 'transparent', border: '1px solid var(--border)',
                color: editing ? '#92400e' : 'var(--text-muted)', padding: '2px 8px', fontSize: '0.75rem' }}
              onClick={() => setEditing(e => !e)}
              title="실제 배정 수정"
            >
              {editing ? '✕' : '✏️'}
            </button>
          </div>
        </td>
      </tr>
      {editing && (
        <EditPanel
          session={s}
          persons={persons}
          ushers={ushers}
          overrides={overrides}
          onSave={handleSave}
          onCancel={() => setEditing(false)}
        />
      )}
    </>
  );
}

function MonthTable({ sessions, monthName, id, overrides, persons, ushers, onOverride }) {
  if (!sessions || sessions.length === 0) return null;
  return (
    <div className="schedule-section" id={id}>
      <div className="month-title">📅 {monthName}</div>
      <table className="schedule-table">
        <thead>
          <tr>
            <th style={{ width: 80 }}>날짜</th>
            <th style={{ width: 72 }}>요일</th>
            <th>사회자</th>
            <th>기도자</th>
            <th>로비 안내위원</th>
          </tr>
        </thead>
        <tbody>
          {sessions.map((s, i) => (
            <SessionRow
              key={i}
              s={s}
              overrides={overrides}
              persons={persons}
              ushers={ushers}
              onOverride={onOverride}
            />
          ))}
        </tbody>
      </table>
    </div>
  );
}

export default function ScheduleView({ schedule, overrides, year, facilitators, prayers, ushers, persons, onOverride }) {
  const [currentMonth, setCurrentMonth] = useState(new Date().getMonth());
  const [viewAll, setViewAll] = useState(false);
  const exportRef = useRef(null);

  const byMonth = groupScheduleByMonth(schedule);
  const months = Object.keys(byMonth).map(Number).sort((a, b) => a - b);

  async function exportMonth(month) {
    const html2canvas = (await import('html2canvas')).default;
    const el = document.getElementById(`month-${month}`);
    if (!el) return;
    const canvas = await html2canvas(el, { backgroundColor: '#ffffff', scale: 2 });
    const link = document.createElement('a');
    link.download = `${year}년_${MONTH_NAMES[month]}_예배스케줄.png`;
    link.href = canvas.toDataURL('image/png');
    link.click();
  }

  async function exportAll() {
    const html2canvas = (await import('html2canvas')).default;
    const el = exportRef.current;
    if (!el) return;
    const canvas = await html2canvas(el, { backgroundColor: '#ffffff', scale: 2 });
    const link = document.createElement('a');
    link.download = `${year}년_전체_예배스케줄.png`;
    link.href = canvas.toDataURL('image/png');
    link.click();
  }

  if (schedule.length === 0) {
    return (
      <div className="card">
        <div className="empty-state"><div className="empty-icon">📋</div><div>스케줄이 없습니다. 사회자를 추가해주세요.</div></div>
      </div>
    );
  }

  const tableProps = { overrides, persons, ushers, onOverride };

  return (
    <div>
      <div className="schedule-controls">
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <div className="month-nav">
            <button className="month-nav-btn" onClick={() => { setCurrentMonth(m => Math.max(0, m - 1)); setViewAll(false); }}>‹</button>
            <span className="month-display">{MONTH_NAMES[currentMonth]}</span>
            <button className="month-nav-btn" onClick={() => { setCurrentMonth(m => Math.min(11, m + 1)); setViewAll(false); }}>›</button>
          </div>
          <button className={`btn btn-sm ${viewAll ? 'btn-primary' : 'btn-outline'}`} onClick={() => setViewAll(v => !v)}>
            {viewAll ? '월별 보기' : '전체 보기'}
          </button>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          {!viewAll && (
            <button className="btn btn-secondary btn-sm" onClick={() => exportMonth(currentMonth)}>
              ⬇ 이미지 저장 ({MONTH_NAMES[currentMonth]})
            </button>
          )}
          <button className="btn btn-success btn-sm" onClick={exportAll}>⬇ 전체 이미지 저장</button>
        </div>
      </div>

      <div className="alert alert-info" style={{ marginBottom: 16 }}>
        ✏️ 각 행 오른쪽의 <strong>연필 버튼</strong>을 눌러 실제 배정으로 수정할 수 있습니다.
        수정된 행은 <span style={{ background: '#fef3c7', color: '#92400e', padding: '1px 5px', borderRadius: 4, fontSize: '0.85rem', fontWeight: 700 }}>수정</span> 표시가 붙습니다.
      </div>

      <div ref={exportRef} style={{ background: '#fff', padding: viewAll ? 16 : 0 }}>
        {viewAll ? (
          <div className="all-months">
            {months.map(m => (
              <MonthTable key={m} id={`month-${m}`} sessions={byMonth[m]}
                monthName={`${year}년 ${MONTH_NAMES[m]}`} {...tableProps} />
            ))}
          </div>
        ) : (
          <div id={`month-${currentMonth}`}>
            {byMonth[currentMonth]
              ? <MonthTable sessions={byMonth[currentMonth]} monthName={`${year}년 ${MONTH_NAMES[currentMonth]}`} {...tableProps} />
              : <div className="card"><div className="empty-state"><div className="empty-icon">📅</div><div>이 달에는 예배 세션이 없습니다.</div></div></div>
            }
          </div>
        )}
      </div>
    </div>
  );
}
